import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';
/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {{ x: number, y: number } | undefined} */
  #preservedScrollPosition;

  /** @type {number | undefined} */
  #viewportLockTimer;

  /** @type {{ x: number, y: number, bodyStyle: string | null, headerSection: HTMLElement | null, headerSectionStyle: string | null, header: HTMLElement | null, headerStyle: string | null, wasScrolled: boolean, headerObserver: MutationObserver | null, headerPlaceholder: HTMLElement | null } | undefined} */
  #viewportLockState;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Explicitly selects a variant card used by the Custom Product Info picker.
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  selectVariant(event) {
    const label = event.target instanceof Element
      ? event.target.closest('.variant-option__button-label')
      : null;
    const quantityInput = label?.querySelector('input[type="radio"][data-quantity-option]');
    if (quantityInput instanceof HTMLInputElement) {
      // Quantity cards are handled locally. Prevent the native label focus jump;
      // the delayed sync below applies the radio and cart quantity state.
      event.preventDefault();
      this.lockViewportPosition(400);
      // Run after all click handlers so an older radio cannot restore its
      // checked state after our selection.
      setTimeout(() => this.selectQuantity(quantityInput), 0);
      return;
    }

    event.preventDefault();

    this.lockViewportPosition();

    const input = label?.querySelector('input[type="radio"][data-variant-id]');
    if (!(input instanceof HTMLInputElement) || input.getAttribute('aria-disabled') === 'true') return;

    this.setCustomVariantSelection(input);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {HTMLInputElement} selectedInput */
  selectQuantity(selectedInput) {
    this.querySelectorAll('input[type="radio"][data-quantity-option]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const isSelected = input === selectedInput;
      input.checked = isSelected;
      input.toggleAttribute('checked', isSelected);
      input.dataset.currentChecked = String(isSelected);
      input.closest('.variant-option__button-label')?.setAttribute('data-quantity-selected', String(isSelected));
    });

    const quantity = Number(selectedInput.dataset.quantityOption) || 1;
    const productForm = document.getElementById(`BuyButtons-ProductForm-${this.dataset.sectionId}`);
    const formQuantityInput = productForm?.querySelector('[name="quantity"]');
    if (formQuantityInput instanceof HTMLInputElement) {
      formQuantityInput.value = String(quantity);
    }

    if (typeof window.updatePrice === 'function') window.updatePrice(quantity);
  }

  lockViewportPosition(duration = 1400) {
    if (this.#viewportLockTimer) clearTimeout(this.#viewportLockTimer);

    if (!this.#viewportLockState) {
      const body = document.body;
      const x = window.scrollX;
      const y = window.scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const headerSection = document.querySelector('.header-section');
      const header = document.getElementById('header-component');
      const wasScrolled = header?.classList.contains('scrolled') ?? y > 50;
      let headerPlaceholder = null;

      if (headerSection instanceof HTMLElement && wasScrolled) {
        headerPlaceholder = document.createElement('div');
        headerPlaceholder.setAttribute('aria-hidden', 'true');
        headerPlaceholder.style.height = `${headerSection.getBoundingClientRect().height}px`;
        headerPlaceholder.style.width = '100%';
        headerSection.insertAdjacentElement('afterend', headerPlaceholder);
      }

      this.#viewportLockState = {
        x,
        y,
        bodyStyle: body.getAttribute('style'),
        headerSection: headerSection instanceof HTMLElement ? headerSection : null,
        headerSectionStyle: headerSection?.getAttribute('style') ?? null,
        header: header instanceof HTMLElement ? header : null,
        headerStyle: header?.getAttribute('style') ?? null,
        wasScrolled,
        headerObserver: null,
        headerPlaceholder,
      };

      body.style.position = 'fixed';
      body.style.top = `${-y}px`;
      body.style.left = `${-x}px`;
      body.style.right = 'auto';
      body.style.width = `calc(100% - ${scrollbarWidth}px)`;

      if (headerSection instanceof HTMLElement && wasScrolled) {
        headerSection.style.setProperty('position', 'fixed', 'important');
        headerSection.style.setProperty('top', '0', 'important');
        headerSection.style.setProperty('left', '0', 'important');
        headerSection.style.setProperty('right', 'auto', 'important');
        headerSection.style.setProperty('width', `calc(100% - ${scrollbarWidth}px)`, 'important');
        headerSection.style.setProperty('z-index', '1000', 'important');
      }
      if (header instanceof HTMLElement && wasScrolled) {
        header.style.setProperty('opacity', '1', 'important');
        header.style.setProperty('transition', 'none', 'important');
        const keepScrolledState = () => header.classList.toggle('scrolled', wasScrolled);
        keepScrolledState();
        const observer = new MutationObserver(keepScrolledState);
        observer.observe(header, { attributes: true, attributeFilter: ['class'] });
        this.#viewportLockState.headerObserver = observer;
      }
    }

    this.#viewportLockTimer = setTimeout(() => this.releaseViewportPosition(), duration);
  }

  releaseViewportPosition() {
    const state = this.#viewportLockState;
    if (!state) return;

    state.headerObserver?.disconnect();
    state.headerPlaceholder?.remove();

    const restoreStyle = (element, style) => {
      if (!element) return;
      if (style == null) element.removeAttribute('style');
      else element.setAttribute('style', style);
    };

    restoreStyle(document.body, state.bodyStyle);
    restoreStyle(state.headerSection, state.headerSectionStyle);
    restoreStyle(state.header, state.headerStyle);

    this.#viewportLockState = undefined;
    this.#viewportLockTimer = undefined;
    window.scrollTo({ left: state.x, top: state.y, behavior: 'instant' });
    if (state.header) {
      state.header.classList.toggle('scrolled', state.wasScrolled);
      requestAnimationFrame(() => state.header?.classList.toggle('scrolled', window.scrollY > 50));
    }
  }

  setCustomVariantSelection(selectedInput) {
    const radios = this.querySelectorAll('input[type="radio"][data-variant-id]');

    radios.forEach((radio) => {
      if (!(radio instanceof HTMLInputElement) || radio.name !== selectedInput.name) return;

      const isSelected = radio === selectedInput;
      radio.checked = isSelected;
      radio.dataset.currentChecked = String(isSelected);
      radio.toggleAttribute('checked', isSelected);
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.target instanceof HTMLInputElement && event.target.dataset.quantityOption) {
      this.selectQuantity(event.target);
      return;
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    ///
    const parent = selectedOption.closest('.variant-option__button-label-property');
    const quantityInput = parent?.querySelector('quantity-selector-component input[type="number"]');
    if (quantityInput) {
      let quantity = Number(quantityInput.value);
      if (!quantity || quantity === 0) {
        quantity = 0;
        quantityInput.value = 0;
      }
      const variantId = selectedOption.dataset.variantId;
      if (variantId) {
        const quantities = JSON.parse(sessionStorage.getItem('variantQuantities') || '{}');
        quantities[variantId] = quantity;
        sessionStorage.setItem(
          'variantQuantities',
          JSON.stringify(quantities)
        );
      }
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    if (this.dataset.customProductInfo === 'true' && !loadsNewProduct) {
      this.#preservedScrollPosition = { x: window.scrollX, y: window.scrollY };
    }

    const isCustomProductInfo = this.dataset.customProductInfo === 'true';
    if (!isCustomProductInfo || loadsNewProduct) {
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);
    }

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (variantId) {
      // Amount cards on Custom Product Info are variants (1 Pouch, 2 Pouches,
      // etc.). Update their visual state and cart form immediately instead of
      // waiting for the asynchronous section-rendering response.
      if (selectedOption instanceof HTMLInputElement) {
        this.setCustomVariantSelection(selectedOption);
      }

      const productForm = document.getElementById(`BuyButtons-ProductForm-${this.dataset.sectionId}`);
      const variantIdInput = productForm?.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement) {
        variantIdInput.value = variantId;
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href && isCustomProductInfo) {
      history.replaceState({}, '', url.toString());
    } else if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        // Start from the latest URL so a Skio selling-plan change that happened
        // after this variant click is not overwritten by a stale URL snapshot.
        const latestUrl = new URL(window.location.href);

        if (variantId) {
          latestUrl.searchParams.set('variant', variantId);
        } else {
          latestUrl.searchParams.delete('variant');
        }

        if (loadsNewProduct && newUrl) latestUrl.pathname = newUrl;

        history.replaceState({}, '', latestUrl.toString());
      });
    }

    if (variantId && isCustomProductInfo) {
      const localHtml = document.implementation.createHTMLDocument('');
      this.dispatchEvent(
        new VariantUpdateEvent(
          {
            id: Number(variantId),
            available: selectedOption.dataset.optionAvailable !== 'false',
          },
          selectedOption.dataset.optionValueId ?? '',
          {
            html: localHtml,
            productId: this.dataset.productId ?? '',
          }
        )
      );
    }

    if (variantId) {
      document.dispatchEvent(new CustomEvent('variantChanged', { detail: { variantId } }));
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const responseVariantPicker = this.getResponseVariantPicker(html);
        const textContent = responseVariantPicker?.querySelector('script[type="application/json"]')?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          this.restorePreservedScrollPosition();

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
        requestAnimationFrame(() => {
          this.restorePreservedScrollPosition();
          window.restoreVariantQuantity();
          window.updateVariantUI();
        });

        if (this.#preservedScrollPosition) {
          setTimeout(() => this.restorePreservedScrollPosition(true), 100);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  restorePreservedScrollPosition(clear = false) {
    const position = this.#preservedScrollPosition;
    if (!position) return;

    window.scrollTo({ left: position.x, top: position.y, behavior: 'instant' });
    if (clear) this.#preservedScrollPosition = undefined;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = this.getResponseVariantPicker(newHtml);

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Finds this exact variant picker in a full-page or section response.
   * Custom product templates can contain multiple product information sections.
   * @param {Document} html
   * @returns {Element | null}
   */
  getResponseVariantPicker(html) {
    const tagName = this.tagName.toLowerCase();
    const sectionId = this.dataset.sectionId;
    const blockId = this.dataset.blockId;

    if (sectionId && blockId) {
      const selector = `${tagName}[data-section-id="${CSS.escape(sectionId)}"][data-block-id="${CSS.escape(blockId)}"]`;
      const exactPicker = html.querySelector(selector);
      if (exactPicker) return exactPicker;
    }

    return html.querySelector(tagName);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}


if (document.body.classList.contains('pdp-lemon-drop-pdp')) {
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('a, button, input, select, textarea, label, [role="button"], [contenteditable]')) return;

      const x = window.scrollX;
      const y = window.scrollY;
      let restoring = false;

      const preservePosition = () => {
        if (restoring || (window.scrollX === x && window.scrollY === y)) return;
        restoring = true;
        window.scrollTo({ left: x, top: y, behavior: 'instant' });
        restoring = false;
      };

      window.addEventListener('scroll', preservePosition, { passive: true });
      window.setTimeout(() => {
        preservePosition();
        window.removeEventListener('scroll', preservePosition);
      }, 300);
    },
    { capture: true }
  );

  document.addEventListener(
    'touchstart',
    (startEvent) => {
      if (startEvent.touches.length !== 1 || !(startEvent.target instanceof Element)) return;
      if (!startEvent.target.closest('.new-main-product.custom-template-new')) return;
      if (startEvent.target.closest('a, button, input, select, textarea, label, [role="button"], [contenteditable]')) {
        return;
      }

      const startTouch = startEvent.touches[0];
      if (!startTouch) return;
      const startX = startTouch.clientX;
      const startY = startTouch.clientY;
      const identifier = startTouch.identifier;
      let moved = false;

      const cleanup = () => {
        document.removeEventListener('touchmove', handleMove, true);
        document.removeEventListener('touchend', handleEnd, true);
        document.removeEventListener('touchcancel', handleCancel, true);
      };
      const handleMove = (moveEvent) => {
        const touch = Array.from(moveEvent.touches).find((item) => item.identifier === identifier);
        if (!touch) return;
        if (Math.hypot(touch.clientX - startX, touch.clientY - startY) > 8) moved = true;
      };
      const handleEnd = (endEvent) => {
        const touch = Array.from(endEvent.changedTouches).find((item) => item.identifier === identifier);
        if (!touch) return;
        cleanup();
        if (moved) return;

        const picker = document.querySelector('variant-picker[data-custom-product-info="true"]');
        if (picker instanceof VariantPicker) picker.lockViewportPosition(400);
      };
      const handleCancel = () => cleanup();

      document.addEventListener('touchmove', handleMove, { capture: true, passive: true });
      document.addEventListener('touchend', handleEnd, { capture: true, passive: true });
      document.addEventListener('touchcancel', handleCancel, { capture: true, passive: true });
    },
    { capture: true, passive: true }
  );
}
